import TransactionRow, { type RowMode, type RowSource } from "@/components/transactions/TransactionRow";

interface DayTransaction {
  id: string;
  description: string;
  amount: number;
  categoryName: string;
  categoryColor: string;
  paymentLabel: string;
  dateLabel?: string;
  source: RowSource;
  sourceLabel: string;
  installment?: { current: number; total: number };
  canEdit: boolean;
}

interface DayGroupProps {
  label: string;
  itemLabel: string;
  currencySymbol: string;
  locale: "en-US" | "he-IL";
  mode: RowMode;
  transactions: DayTransaction[];
  selectedIds: Set<string>;
  showSourcePill: boolean;
  onToggleSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  editTooltip: string;
  editDisabledTooltip: string;
  deleteTooltip: string;
}

export default function DayGroup({
  label,
  itemLabel,
  currencySymbol,
  locale,
  mode,
  transactions,
  selectedIds,
  showSourcePill,
  onToggleSelect,
  onEdit,
  onDelete,
  editTooltip,
  editDisabledTooltip,
  deleteTooltip,
}: DayGroupProps) {
  const dayExpense = transactions
    .filter((transaction) => transaction.amount < 0)
    .reduce((sum, transaction) => sum + Math.abs(transaction.amount), 0);

  return (
    <div className="flex flex-col gap-[8px]">
      {mode === "grouped" ? (
        <div className="flex items-center justify-between px-2 pb-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-primary)]">
            {label}
          </span>
          <span
            className="text-[11px] font-normal text-[var(--text-muted)]"
            style={{ fontVariantNumeric: "tabular-nums", fontFeatureSettings: '"tnum"', letterSpacing: "-0.01em" }}
          >
            {transactions.length} {itemLabel} · <span className="text-[var(--expense)]">−{currencySymbol}{Math.round(dayExpense).toLocaleString(locale)}</span>
          </span>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-[12px] border border-[var(--border-color)] bg-[var(--bg-secondary)]">
        {transactions.map((transaction, index) => (
          <div key={transaction.id} className={index === transactions.length - 1 ? "[&>div]:border-b-0" : ""}>
            <TransactionRow
              id={transaction.id}
              description={transaction.description}
              amount={transaction.amount}
              categoryName={transaction.categoryName}
              categoryColor={transaction.categoryColor}
              paymentLabel={transaction.paymentLabel}
              dateLabel={transaction.dateLabel}
              source={transaction.source}
              sourceLabel={transaction.sourceLabel}
              installment={transaction.installment}
              currencySymbol={currencySymbol}
              locale={locale}
              mode={mode}
              selected={selectedIds.has(transaction.id)}
              showSourcePill={showSourcePill}
              canEdit={transaction.canEdit}
              editTooltip={editTooltip}
              editDisabledTooltip={editDisabledTooltip}
              deleteTooltip={deleteTooltip}
              onToggleSelect={onToggleSelect}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
